import KitBadge from '@omni/kit/components/KitBadge';
import KitFadeView from '@omni/kit/components/KitFadeView';
import { IconName } from '@omni/kit/components/KitIcon';
import KitIcon from '@omni/kit/components/KitIcon';
import KitMenu from '@omni/kit/components/KitMenu';
import { MenuItem } from '@omni/kit/components/KitMenu/types';
import KitText from '@omni/kit/components/KitText';
import GetSizeClassV2 from '@omni/kit/contexts/SizeClassContext/GetSizeClassV2';
import { SizeClassV2 } from '@omni/kit/contexts/SizeClassContext/types';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Spacing from '@omni/kit/theming/Spacing';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { MEDIA_TYPE } from '../../../../../../types';
import { SubtitleTrack } from '../../../../../PlayerVideo/PlayerVideo.types';
import { getSubtitleLanguage } from '../../../../../PlayerVideo/util/cachedSubtitle';
import { PlayerControlsProps } from '../../../../types';
import PlayerButton, { PlayerButtonSizes } from '../PlayerButton';
import { SettingsControlRef } from './SettingsControl.types';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'waves-menu-item': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          text?: string;
          subtext?: string;
          danger?: boolean;
          selected?: boolean;
          disabled?: boolean;
          onClick?: () => void;
        },
        HTMLElement
      >;
    }
  }
}

const appDebug = require('debug')(
  'tca:KitPlayer:components:Controls:PlayerControlTypes:DefaultControls:SettingsControl'
);

export interface SettingsControlProps {
  hasAudio?: boolean;
  hasVideo?: boolean;
  isPlayerReady?: boolean;
  isVisible?: boolean;
  mediaType: MEDIA_TYPE;

  onSelectSubtitleTrack?: (trackIndex: number, language?: string) => void;
  onSelectQualityLevel?: (level: number, label: string) => void;
  onChangeMediaType?: (mediaType: MEDIA_TYPE) => void;
  onPlaybackSpeedChange?: (speed: number) => void;
  playerVideoRef?: PlayerControlsProps['playerVideoRef'];
  playerContainerDimensions?: { width: number; height: number };
  onOpen?: (value: boolean) => void;
}

/**
 * SettingsControlMenuItemProps interface will handle the cases of
 * nested menu items
 */
interface SettingsControlMenuItemProps extends MenuItem {
  submenu?: MenuItem[];
}

const MENU_STANDARD_MAX_HEIGHT = 342;
const MENU_STANDARD_WIDTH = 240;

const SettingsControl = React.forwardRef<
  SettingsControlRef,
  SettingsControlProps
>(
  (
    {
      hasAudio,
      hasVideo,
      isPlayerReady = false,
      isVisible = true,
      mediaType,
      playerVideoRef,
      playerContainerDimensions,

      onSelectSubtitleTrack,
      onSelectQualityLevel,
      onChangeMediaType,
      onPlaybackSpeedChange,
      onOpen,
    }: SettingsControlProps,
    ref
  ): ReactElement => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const [currentLanguage, setCurrentLanguage] = useState(t('settings:off'));
    const [currentSpeed, setCurrentSpeed] = useState(t('settings:1x'));
    const [settingsQualityLabel, setSettingsQualityLabel] = useState('');
    const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrack[]>([]);

    // Get quality badge based on resolution
    const getQualityBadge = useCallback((label: string) => {
      if (!label) return null;

      // Extract height value from quality label (e.g., "1080p" -> 1080)
      const heightMatch = label.match(/(\d+)p/);

      if (heightMatch && heightMatch[1]) {
        const height = parseInt(heightMatch[1], 10);

        if (height >= 4320 || label.includes('8K')) {
          return '8K';
        } else if (
          height >= 2160 ||
          label.includes('4K') ||
          label.includes('UHD')
        ) {
          return '4K';
        } else if (height >= 1080) {
          return 'HD';
        }
      } else if (label.includes('8K') || label.includes('4320')) {
        return '8K';
      } else if (
        label.includes('2160') ||
        label.includes('4K') ||
        label.includes('UHD')
      ) {
        return '4K';
      }

      return null;
    }, []);

    // Check if current quality is HD or 4K
    const currentQualityBadge = useMemo(
      () => getQualityBadge(settingsQualityLabel),
      [getQualityBadge, settingsQualityLabel]
    );

    const isHDorHigherQuality = useMemo(
      () => Boolean(currentQualityBadge),
      [currentQualityBadge]
    );

    const playerContainerHeight = playerContainerDimensions?.height ?? 0;
    const playerContainerWidth = playerContainerDimensions?.width ?? 0;

    const sizeClass = GetSizeClassV2(playerContainerWidth);
    const isSmallScreen = sizeClass <= SizeClassV2.XS;

    const calculateMenuMaxHeight = useMemo(() => {
      // top space = padding
      const topSpace = isSmallScreen ? Spacing.l : Spacing.xl;
      // bottp, space = padding + fixed menu position
      const bottomSpace = isSmallScreen ? Spacing.l + 40 : Spacing.xl + 40;
      const availableSpaceToRenderMenu =
        playerContainerHeight - topSpace - bottomSpace;

      if (availableSpaceToRenderMenu > MENU_STANDARD_MAX_HEIGHT) {
        return MENU_STANDARD_MAX_HEIGHT;
      }

      return availableSpaceToRenderMenu;
    }, [playerContainerHeight, isSmallScreen]);

    useEffect(() => {
      if (playerVideoRef?.current) {
        const isAutoQuality = playerVideoRef.current.isAutoQualityEnabled();
        const qualityLevels = playerVideoRef.current.getQualityLevels();
        const currentLevel = playerVideoRef.current.getCurrentQualityLevel();
        const currentQualityLabel =
          playerVideoRef.current.getCurrentQualityLabel();

        const findLevel = (qualityLevel: { level: number }) =>
          qualityLevel.level === currentLevel;

        const currentLevelInfo = qualityLevels.find(findLevel);

        if (isAutoQuality) {
          setSettingsQualityLabel(
            currentQualityLabel ? currentQualityLabel : t('settings:auto')
          );
        } else {
          setSettingsQualityLabel(currentLevelInfo?.label ?? '');
        }
      }
    }, [playerVideoRef, t, mediaType, settingsQualityLabel]);

    const [currentMenuItems, setCurrentMenuItems] = useState<
      SettingsControlMenuItemProps[]
    >([]);
    const [parentMenuItem, setParentMenuItem] =
      useState<SettingsControlMenuItemProps | null>(null);
    const buttonRef = useRef<View>(null);

    const handleSelectedPlaybackSpeed = useCallback(
      (speed: number) => {
        const speedText = speed === 1 ? t('settings:1x') : `${speed}x`;
        setCurrentSpeed(speedText);
        onPlaybackSpeedChange?.(speed);
        setIsMenuOpen(false);
      },
      [onPlaybackSpeedChange, t]
    );

    const handleSelectedSubtitleTrack = useCallback(
      (index: number, track?: SubtitleTrack) => {
        if (index !== -1) {
          onSelectSubtitleTrack?.(index, track?.lang);
          setCurrentLanguage(track?.name || t('settings:off'));
        } else {
          onSelectSubtitleTrack?.(-1);
          setCurrentLanguage(t('settings:off'));
        }
      },
      [onSelectSubtitleTrack, t]
    );

    const updateIsMenuOpen = useCallback(
      (open: boolean) => {
        setIsMenuOpen(open);
        onOpen?.(open);
      },
      [onOpen]
    );

    const handleCloseAnimation = useCallback(() => {
      if (!isMenuOpen) {
        onOpen?.(false);
      }
    }, [isMenuOpen, onOpen]);

    useImperativeHandle(
      ref,
      () => ({
        isMenuOpen,
        toggleMenu: (open?: boolean) => setIsMenuOpen(open ?? !isMenuOpen),
        toggleSubtitle: () => {
          if (!playerVideoRef?.current) return;

          if (!subtitleTracks?.length) return;

          if (currentLanguage === t('settings:off')) {
            const firstSubtitle = subtitleTracks?.[0];
            handleSelectedSubtitleTrack(0, firstSubtitle);
          } else {
            handleSelectedSubtitleTrack(-1);
          }

          setIsMenuOpen(false);
        },
        handleSelectedPlaybackSpeed,
        handleSelectedSubtitleTrack,
      }),
      [
        currentLanguage,
        isMenuOpen,
        playerVideoRef,
        t,
        subtitleTracks,
        handleSelectedPlaybackSpeed,
        handleSelectedSubtitleTrack,
      ]
    );

    const mediaTypeMenuItem = useMemo(() => {
      if (mediaType === 'video' && hasAudio) {
        return [
          {
            text: t('settings:switchTo', { mediaType: 'audio' }),
            icon: 'listen' as IconName,
            onSelect: () => {
              onChangeMediaType?.('audio');
              setIsMenuOpen(false);
            },
          },
        ];
      }

      if (mediaType === 'audio' && hasVideo) {
        return [
          {
            text: t('settings:switchTo', { mediaType: 'video' }),
            icon: 'play-circle' as IconName,
            onSelect: () => {
              onChangeMediaType?.('video');
              setIsMenuOpen(false);
            },
          },
        ];
      }

      return [];
    }, [hasAudio, hasVideo, mediaType, onChangeMediaType, t]);

    const subtitlesMenuItem = useMemo(() => {
      if (mediaType !== 'video' || !subtitleTracks?.length) {
        return [];
      }

      return [
        {
          text: t('settings:subtitles'),
          icon: 'subtitles' as IconName,
          badge: currentLanguage,
          submenu: [
            {
              text: t('settings:off'),
              selected: currentLanguage === t('settings:off'),
              onSelect: () => {
                if (!playerVideoRef?.current) return;

                handleSelectedSubtitleTrack(-1);
                setIsMenuOpen(false);
              },
            },
            ...subtitleTracks.map((subtitle, index) => ({
              text: subtitle.name,
              selected: currentLanguage === subtitle.name,
              onSelect: () => {
                if (!playerVideoRef?.current) return;

                handleSelectedSubtitleTrack(index, subtitle);
                setIsMenuOpen(false);
              },
            })),
          ],
        },
      ];
    }, [
      currentLanguage,
      mediaType,
      playerVideoRef,
      t,
      subtitleTracks,
      handleSelectedSubtitleTrack,
    ]);

    const playbackSpeedMenuItem = useMemo(
      () => [
        {
          text: t('settings:playbackSpeed'),
          icon: 'playback-speed' as IconName,
          badge: currentSpeed,
          submenu: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => ({
            text: speed === 1 ? t('settings:normal') : t(`settings:${speed}x`),
            selected:
              currentSpeed === (speed === 1 ? t('settings:1x') : `${speed}x`),
            onSelect: () => handleSelectedPlaybackSpeed(speed),
          })),
        },
      ],
      [currentSpeed, t, handleSelectedPlaybackSpeed]
    );

    const qualityMenuItem = useMemo(() => {
      const qualityLevels = playerVideoRef?.current?.getQualityLevels();

      if (mediaType !== 'video' || !qualityLevels?.length) {
        return [];
      }

      return [
        {
          text: t('settings:quality'),
          icon: 'settings-filter' as IconName,
          badge: settingsQualityLabel,
          submenu: qualityLevels.map((quality) => {
            const isAutoQualityMode = quality.level === -1;
            const currentLevel =
              playerVideoRef?.current?.getCurrentQualityLevel();
            const isSelected = currentLevel === quality.level;
            const qualityBadge = getQualityBadge(quality.label);

            return {
              text: quality.label,
              selected: isSelected,
              badge: qualityBadge,
              onSelect: () => {
                if (!playerVideoRef?.current) return;

                onSelectQualityLevel?.(quality.level, quality.label);

                if (isAutoQualityMode) {
                  setTimeout(() => {
                    if (!playerVideoRef.current) return;

                    const currentQualityLabel =
                      playerVideoRef.current.getCurrentQualityLabel();
                    setSettingsQualityLabel(
                      currentQualityLabel || t('settings:auto')
                    );
                  }, 1000);
                } else {
                  setSettingsQualityLabel(quality.label);
                }

                setIsMenuOpen(false);
              },
            };
          }),
        },
      ];
    }, [
      mediaType,
      playerVideoRef,
      settingsQualityLabel,
      t,
      onSelectQualityLevel,
      getQualityBadge,
    ]);

    const menuItems: SettingsControlMenuItemProps[] = useMemo(
      () => [
        ...mediaTypeMenuItem,
        ...subtitlesMenuItem,
        ...playbackSpeedMenuItem,
        ...qualityMenuItem,
      ],
      [
        mediaTypeMenuItem,
        subtitlesMenuItem,
        playbackSpeedMenuItem,
        qualityMenuItem,
      ]
    );

    const handleMenuSelect = useCallback(
      (text: string) => {
        const selectedItem = currentMenuItems.find(
          (item) => item.text === text
        );

        if (selectedItem?.submenu) {
          setParentMenuItem(selectedItem);
          setCurrentMenuItems(selectedItem.submenu);
        } else if (text === parentMenuItem?.text) {
          setParentMenuItem(null);
          setCurrentMenuItems(menuItems);
        }
      },
      [currentMenuItems, parentMenuItem, menuItems]
    );

    const handleClickOutside = useCallback(
      (event: MouseEvent | TouchEvent) => {
        if (buttonRef.current) {
          const node = buttonRef.current as unknown;

          if (
            node instanceof HTMLElement &&
            !node.contains(event.target as Node)
          ) {
            if (isMenuOpen) {
              setIsMenuOpen(false);
            }
          }
        }
      },
      [buttonRef, isMenuOpen]
    );

    /** ------------ USE EFFECTS ------ */

    useEffect(() => {
      if (!isMenuOpen) {
        setParentMenuItem(null);
      }

      setCurrentMenuItems(menuItems);
    }, [isMenuOpen, menuItems]);

    useEffect(() => {
      if (!isPlayerReady) return;

      const tracks = playerVideoRef?.current?.getSubtitleTracks();

      if (!tracks) return;

      setSubtitleTracks(tracks);
    }, [playerVideoRef, isPlayerReady]);

    /** attempt to restore cached subtitle language */
    useEffect(() => {
      if (!isPlayerReady || !subtitleTracks) return;

      async function restoreSubitleCache() {
        const cachedSubitleLang = await getSubtitleLanguage();

        if (!cachedSubitleLang) return;

        subtitleTracks.find((track, index) => {
          if (track.lang === cachedSubitleLang) {
            appDebug('cachedSubitleLang found. Turn on subtitle', track.name);
            handleSelectedSubtitleTrack(index, track);

            return true;
          }
        });
      }

      restoreSubitleCache();
    }, [
      playerVideoRef,
      isPlayerReady,
      subtitleTracks,
      handleSelectedSubtitleTrack,
    ]);

    useEffect(() => {
      if (Platform.OS === 'web') {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }

      return () => {
        if (Platform.OS === 'web') {
          document.removeEventListener('mousedown', handleClickOutside);
          document.removeEventListener('touchstart', handleClickOutside);
        }
      };
    }, [handleClickOutside]);

    return (
      <KitFadeView show={isVisible} keepElementSize>
        <View ref={buttonRef} style={styles.container}>
          <KitMenu
            menuContainerStyle={styles.menuContainer}
            menuItemsContainerStyle={{
              ...styles.menuItemsContainer,
              maxHeight: playerContainerDimensions?.height
                ? calculateMenuMaxHeight
                : undefined,
              overflowX: 'hidden',
              overflowY: 'scroll',
              scrollbarWidth: 'none',
            }}
            show={isMenuOpen}
            onClose={handleCloseAnimation}
          >
            {parentMenuItem?.text ? (
              <KitText bold black style={styles.menuHeader}>
                {parentMenuItem.icon ? (
                  <div slot='left'>
                    <KitIcon size={20} name={parentMenuItem.icon} />
                  </div>
                ) : null}
                {parentMenuItem.text}
              </KitText>
            ) : null}
            {currentMenuItems.map((item, index) => (
              <waves-menu-item
                key={index}
                text={item.text}
                {...(item.selected ? { selected: true } : {})}
                onClick={() => {
                  if (item.onSelect) {
                    item.onSelect(item.text);
                  }

                  handleMenuSelect(item.text);
                }}
              >
                {item.icon ? (
                  <div slot='left'>
                    <KitIcon size={20} name={item.icon} />
                  </div>
                ) : null}
                {item.badge ? (
                  <div slot='right'>
                    <KitBadge
                      text={item.badge}
                      textStyle={{ fontWeight: '600' }}
                    />
                  </div>
                ) : null}
              </waves-menu-item>
            ))}
          </KitMenu>
          <PlayerButton
            icon='settings'
            onPress={() => updateIsMenuOpen(!isMenuOpen)}
            size={
              Platform.OS === 'web'
                ? PlayerButtonSizes.small
                : PlayerButtonSizes.medium
            }
            frameless
            tooltipProps={{
              label: !isMenuOpen ? t('common:settings') : '',
            }}
            badge={isHDorHigherQuality}
            badgeProps={{
              size: 'xs',
              text: currentQualityBadge || '',
              style: {
                position: 'absolute',
                top: Spacing.xs,
                right: Spacing.xs,
              },
              textStyle: { fontWeight: '800' },
            }}
          />
        </View>
      </KitFadeView>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  menuContainer: {
    position: 'absolute',
    bottom: Spacing.xxl + Spacing.s,
    right: 0,
  },
  menuHeader: {
    display: 'flex',
    gap: Spacing.m,
    paddingTop: 6,
    paddingBottom: Spacing.xs,
    paddingHorizontal: Spacing.m,
  },
  menuItemsContainer: {
    width: MENU_STANDARD_WIDTH,
    padding: Spacing.m,
    maxHeight: MENU_STANDARD_MAX_HEIGHT,
  },
});

export default SettingsControl;
